import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, RefreshCw, X } from "lucide-react";
import { Select } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import {
  QUOTATION_PACKAGES_URL, QUOTATION_PACKAGE_URL, QUOTATION_MODULES_URL,
} from "../../constants/api";

const INCLUSION = [
  { value: "included",          label: "Included" },
  { value: "optional_at_extra", label: "Optional (extra cost)" },
  { value: "not_available",     label: "Not available" },
];

const EMPTY = {
  name: "", description: "", line_description: "",
  max_beds: 0, transaction_count_per_year: 0,
  base_price: "", default_discount_amount: "",
  gst_rate: 18, amc_rate: 40, validity_days: 15, sort_order: 0,
  features: [],
  modules:  [],
};

const fmt = (v) => v && Number(v) > 0 ? `₹${Number(v).toLocaleString("en-IN")}` : "—";

export default function PackagesMaster() {
  const { fetch, create, update, remove } = useApiMethods();
  const [packages, setPackages] = useState([]);
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm]     = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm]   = useState(false);

  const load = async () => {
    setLoading(true);
    const [pk, md] = await Promise.all([
      fetch(QUOTATION_PACKAGES_URL),
      fetch(QUOTATION_MODULES_URL),
    ]);
    if (pk?.status === "success") setPackages(pk.data || []);
    if (md?.status === "success") setModuleCatalog(md.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = async (row) => {
    const detail = await fetch(QUOTATION_PACKAGE_URL(row.id));
    if (detail?.status !== "success") return ToastMsg("Failed to load", "error");
    const d = detail.data;
    setForm({
      name: d.name, description: d.description || "",
      line_description: d.line_description || "",
      max_beds: d.max_beds, transaction_count_per_year: d.transaction_count_per_year,
      base_price: d.base_price, default_discount_amount: d.default_discount_amount,
      gst_rate: d.gst_rate, amc_rate: d.amc_rate, validity_days: d.validity_days,
      sort_order: d.sort_order,
      features: (d.features || []).map(f => ({ name: f.name, sort_order: f.sort_order })),
      modules:  (d.modules  || []).map(m => ({
        module_id: m.module_id, inclusion: m.inclusion,
        override_price: m.override_price || "", sort_order: m.sort_order,
      })),
    });
    setEditingId(row.id); setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return ToastMsg("Package name is required", "error");
    const payload = {
      ...form,
      base_price:              form.base_price || 0,
      default_discount_amount: form.default_discount_amount || 0,
      features: form.features.filter(f => (f.name || "").trim()),
      modules:  form.modules.filter(m => m.module_id),
    };
    const res = editingId
      ? await update(QUOTATION_PACKAGE_URL(editingId), payload)
      : await create(QUOTATION_PACKAGES_URL, payload);
    if (res?.status === "success") {
      ToastMsg(`Package ${editingId ? "updated" : "created"}`, "success");
      setShowForm(false); load();
    } else ToastMsg(res?.message || "Failed", "error");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    const res = await remove(QUOTATION_PACKAGE_URL(id));
    if (res?.status === "success") { ToastMsg("Deleted", "success"); load(); }
    else ToastMsg(res?.message || "Failed", "error");
  };

  const addFeature = () => setForm(p => ({ ...p, features: [...p.features, { name: "", sort_order: p.features.length }] }));
  const updFeature = (i, v) => setForm(p => ({ ...p, features: p.features.map((f, idx) => idx === i ? { ...f, name: v } : f) }));
  const delFeature = (i) => setForm(p => ({ ...p, features: p.features.filter((_, idx) => idx !== i) }));

  const addModule = () => setForm(p => ({ ...p, modules: [...p.modules, { module_id: null, inclusion: "included", override_price: "", sort_order: p.modules.length }] }));
  const updModule = (i, key, val) => setForm(p => ({ ...p, modules: p.modules.map((m, idx) => idx === i ? { ...m, [key]: val } : m) }));
  const delModule = (i) => setForm(p => ({ ...p, modules: p.modules.filter((_, idx) => idx !== i) }));

  return (
    <ContainerCard>
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Packages</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Plans used to compose quotations for prospects.</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load}><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /></Button>
            <Button size="sm" onClick={openCreate}><Plus className="h-3.5 w-3.5 mr-1" /> New Package</Button>
          </div>
        </div>

        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 space-y-4">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {editingId ? "Edit Package" : "New Package"}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Package Name *">
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              </Field>
              <Field label='PDF Title (e.g. "eMedHub - HMS & EHR")'>
                <Input value={form.line_description} onChange={e => setForm(p => ({ ...p, line_description: e.target.value }))} />
              </Field>
              <Field label="Sort Order">
                <Input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))} />
              </Field>
              <Field label="Max Beds">
                <Input type="number" value={form.max_beds} onChange={e => setForm(p => ({ ...p, max_beds: e.target.value }))} />
              </Field>
              <Field label="Transactions / year">
                <Input type="number" value={form.transaction_count_per_year} onChange={e => setForm(p => ({ ...p, transaction_count_per_year: e.target.value }))} />
              </Field>
              <Field label="Validity (days)">
                <Input type="number" value={form.validity_days} onChange={e => setForm(p => ({ ...p, validity_days: e.target.value }))} />
              </Field>
              <Field label="Base Price (₹)">
                <Input type="number" value={form.base_price} onChange={e => setForm(p => ({ ...p, base_price: e.target.value }))} />
              </Field>
              <Field label="Default Discount (₹)">
                <Input type="number" value={form.default_discount_amount} onChange={e => setForm(p => ({ ...p, default_discount_amount: e.target.value }))} />
              </Field>
              <Field label="GST %">
                <Input type="number" value={form.gst_rate} onChange={e => setForm(p => ({ ...p, gst_rate: e.target.value }))} />
              </Field>
              <Field label="AMC %">
                <Input type="number" value={form.amc_rate} onChange={e => setForm(p => ({ ...p, amc_rate: e.target.value }))} />
              </Field>
              <div className="md:col-span-3">
                <Field label="Internal Description">
                  <Textarea rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                </Field>
              </div>
            </div>

            {/* Features */}
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Feature Bullets (shown on PDF)</h3>
                <Button size="sm" variant="outline" onClick={addFeature}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
              </div>
              {form.features.length === 0 && <p className="text-xs text-gray-400">No features added</p>}
              <div className="space-y-2">
                {form.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input placeholder="e.g. OPD Management" value={f.name} onChange={e => updFeature(i, e.target.value)} />
                    <button onClick={() => delFeature(i)} className="text-red-500 p-1"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Modules */}
            <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Modules from catalog</h3>
                <Button size="sm" variant="outline" onClick={addModule}><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
              </div>
              {form.modules.length === 0 && <p className="text-xs text-gray-400">No modules linked</p>}
              <div className="space-y-2">
                {form.modules.map((m, i) => (
                  <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                    <div className="md:col-span-5">
                      <Select className="w-full" placeholder="Select module"
                        value={m.module_id}
                        onChange={v => updModule(i, "module_id", v)}
                        showSearch optionFilterProp="label"
                        options={moduleCatalog.map(mc => ({ value: mc.id, label: mc.name }))} />
                    </div>
                    <div className="md:col-span-3">
                      <Select className="w-full" value={m.inclusion}
                        onChange={v => updModule(i, "inclusion", v)}
                        options={INCLUSION} />
                    </div>
                    <div className="md:col-span-3">
                      <Input type="number" placeholder="Override price (₹)"
                        value={m.override_price}
                        onChange={e => updModule(i, "override_price", e.target.value)} />
                    </div>
                    <div className="md:col-span-1 text-right">
                      <button onClick={() => delModule(i)} className="text-red-500 p-1"><X className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button size="sm" onClick={save}>Save Package</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {packages.length === 0 && !loading && (
            <p className="text-sm text-gray-400 col-span-full text-center py-6">No packages yet</p>
          )}
          {packages.map(p => (
            <div key={p.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{p.name}</h3>
                  {p.line_description && <p className="text-xs text-gray-500 mt-0.5">{p.line_description}</p>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="p-1 text-primary hover:bg-primary/10 rounded"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(p.id)} className="p-1 text-red-500 hover:bg-red-500/10 rounded"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                <Meta label="Max Beds" value={p.max_beds || "—"} />
                <Meta label="Txn/year" value={(p.transaction_count_per_year || 0).toLocaleString("en-IN")} />
                <Meta label="Base Price" value={fmt(p.base_price)} />
                <Meta label="Discount" value={fmt(p.default_discount_amount)} />
                <Meta label="GST" value={`${p.gst_rate}%`} />
                <Meta label="AMC" value={`${p.amc_rate}%`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </ContainerCard>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className="font-medium text-gray-800 dark:text-gray-200">{value}</p>
    </div>
  );
}
